import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  api, ApiError,
  type ClientDetail, type ConnectionDetail, type ConnectionStatus,
} from '../api';
import { StatusBadge } from '../components/StatusBadge';
import { Banner, type BannerTone } from '../components/Banner';

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

/** The banner the OAuth callback asks us to show, via `?qbo=`. */
const CALLBACK_BANNERS: Record<string, { tone: BannerTone; text: string }> = {
  connected: {
    tone: 'ok',
    text: 'QuickBooks connected. Tokens are encrypted at rest and never leave the API.',
  },
  needs_confirmation: {
    tone: 'attention',
    text: 'Connected, but the QuickBooks company name does not match this client. Confirm below to activate it.',
  },
  cancelled: {
    tone: 'info',
    text: 'You cancelled the QuickBooks authorization. Nothing was changed.',
  },
};

export function Connector() {
  const { clientId = '' } = useParams();
  const [params, setParams] = useSearchParams();

  const [client, setClient] = useState<ClientDetail | null>(null);
  const [conn, setConn] = useState<ConnectionDetail | null>(null);
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [readout, setReadout] = useState<{ title: string; body: unknown } | null>(null);

  // Read the callback outcome once, then strip it from the URL so a refresh does
  // not re-show a stale banner.
  const [callback] = useState(() => params.get('qbo'));
  useEffect(() => {
    if (params.has('qbo') || params.has('connectionId')) {
      const next = new URLSearchParams(params);
      next.delete('qbo');
      next.delete('connectionId');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setClient(await api.client(clientId));
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'NOT_FOUND'
        ? 'That client does not exist, or is not in your active firm.'
        : 'Could not load this client.');
      setLoaded(true);
      return;
    }
    try {
      const c = await api.connection(clientId);
      setConn(c);
      setStatus(c.status);
    } catch (err) {
      // Not an error: a client with no connection is the normal starting state.
      if (err instanceof ApiError && err.code === 'QBO_NOT_CONNECTED') {
        setConn(null);
        setStatus(null);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not load the connection.');
      }
    }
    setLoaded(true);
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  async function act(name: string, fn: () => Promise<unknown>) {
    setBusy(name);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message} (${err.code})` : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  }

  async function startConnect() {
    setBusy('connect');
    setError(null);
    try {
      // Come back to this exact page. The API validates this against its own and
      // the app origin, so it cannot be turned into an open redirect.
      const { authorizationUrl } = await api.connect(
        clientId,
        `${window.location.origin}/clients/${clientId}`,
      );
      window.location.href = authorizationUrl;
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message} (${err.code})` : 'Could not start the connection.');
      setBusy(null);
    }
  }

  const canManage = client?.capabilities.includes('can_manage_integrations') ?? false;

  if (!loaded) return <p className="spinner-text">Loading…</p>;
  if (error && !client) return <Banner tone="error">{error}</Banner>;
  if (!client) return <Banner tone="error">Client not found.</Banner>;

  const banner = callback ? CALLBACK_BANNERS[callback] : undefined;

  return (
    <>
      <div className="page-head">
        <p className="small" style={{ marginBottom: '.6rem' }}>
          <Link to="/clients">← All clients</Link>
        </p>
        <h1>{client.name}</h1>
        {client.legalName && client.legalName !== client.name && (
          <p className="muted" style={{ margin: 0 }}>{client.legalName}</p>
        )}
      </div>

      {banner && <Banner tone={banner.tone}>{banner.text}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}

      <div className="card">
        <div className="card-head">
          <h2>QuickBooks</h2>
          <StatusBadge status={status} />
          {!canManage && (
            <span className="row-end small muted">Read-only access</span>
          )}
        </div>

        <div className="card-body">
          {/* --- not connected ---------------------------------------------- */}
          {status === null && (
            <div className="stack">
              <p className="muted" style={{ maxWidth: '38rem' }}>
                This client is not connected to QuickBooks. Connecting opens Intuit,
                where you sign in and choose which company to link.
              </p>
              {canManage ? (
                <button onClick={startConnect} disabled={busy === 'connect'}>
                  {busy === 'connect' ? 'Opening Intuit…' : 'Connect QuickBooks'}
                </button>
              ) : (
                <p className="small muted">
                  You do not have permission to manage this integration.
                </p>
              )}
            </div>
          )}

          {/* --- pending: company-name mismatch ----------------------------- */}
          {status === 'pending' && conn && (
            <div className="stack">
              <Banner tone="attention">
                The QuickBooks company name does not match this client, so the
                connection is held for review. This is not a failure — confirm it
                if they are the same business.
              </Banner>
              <dl className="kv">
                <dt>Client name</dt>
                <dd>{client.name}</dd>
                <dt>QuickBooks company</dt>
                <dd><strong>{conn.companyName ?? '—'}</strong></dd>
                <dt>Realm</dt>
                <dd className="mono">{conn.realmId ?? '—'}</dd>
              </dl>
              {canManage && (
                <div className="row">
                  <button
                    onClick={() => act('confirm', () => api.confirm(clientId, conn.connectionId))}
                    disabled={busy !== null}
                  >
                    {busy === 'confirm' ? 'Confirming…' : 'Same business — confirm'}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => act('disconnect', () => api.disconnect(clientId))}
                    disabled={busy !== null}
                  >
                    Discard
                  </button>
                </div>
              )}
            </div>
          )}

          {/* --- needs reauth ----------------------------------------------- */}
          {status === 'needs_reauth' && (
            <div className="stack">
              <Banner tone="error">
                QuickBooks rejected our access. This client’s books cannot be read
                until someone reconnects — usually because the connection was
                removed from inside QuickBooks.
              </Banner>
              {canManage && (
                <button onClick={startConnect} disabled={busy === 'connect'}>
                  {busy === 'connect' ? 'Opening Intuit…' : 'Reconnect QuickBooks'}
                </button>
              )}
            </div>
          )}

          {/* --- connected / failing ---------------------------------------- */}
          {(status === 'connected' || status === 'error') && conn && (
            <div className="stack">
              {status === 'error' && (
                <Banner tone="error">
                  Repeated failures talking to QuickBooks
                  {conn.lastError
                    ? ` — ${conn.lastError.consecutiveFailures} in a row, most recently ${conn.lastError.code}`
                    : ''}
                  . The connection still exists; this is usually Intuit rather than us.
                </Banner>
              )}

              <div>
                <h3>Company</h3>
                <dl className="kv">
                  <dt>Name</dt><dd>{conn.companyName ?? '—'}</dd>
                  <dt>Realm</dt><dd className="mono">{conn.realmId ?? '—'}</dd>
                  <dt>Country</dt><dd>{conn.country ?? '—'}</dd>
                  <dt>Fiscal year starts</dt><dd>{conn.fiscalYearStartMonth ?? '—'}</dd>
                  <dt>Environment</dt><dd>{conn.environment}</dd>
                </dl>
              </div>

              <hr className="divider" />

              <div>
                <h3>Connection health</h3>
                <dl className="kv">
                  <dt>Connected</dt>
                  <dd>
                    {when(conn.connectedAt)}
                    {conn.connectedBy ? ` · ${conn.connectedBy.name}` : ''}
                  </dd>
                  <dt>Last successful read</dt><dd>{when(conn.lastSuccessAt)}</dd>
                  {/* Expiries only. The API never returns token material and this
                      UI must never invent a place to show it. */}
                  <dt>Access token expires</dt>
                  <dd className="mono small">{when(conn.accessTokenExpiresAt)}</dd>
                  <dt>Refresh token expires</dt>
                  <dd className="mono small">{when(conn.refreshTokenExpiresAt)}</dd>
                </dl>
              </div>

              <hr className="divider" />

              <div>
                <h3>Read from QuickBooks</h3>
                <p className="small muted" style={{ marginBottom: '.9rem' }}>
                  On demand. Nothing read here is stored.
                </p>
                <div className="row">
                  <button
                    className="secondary"
                    disabled={busy !== null}
                    onClick={() => act('company', async () =>
                      setReadout({ title: 'Company info', body: await api.qboCompany(clientId) }))}
                  >
                    {busy === 'company' ? 'Reading…' : 'Company info'}
                  </button>
                  <button
                    className="secondary"
                    disabled={busy !== null}
                    onClick={() => act('accounts', async () =>
                      setReadout({ title: 'Chart of accounts', body: await api.qboAccounts(clientId) }))}
                  >
                    {busy === 'accounts' ? 'Reading…' : 'Chart of accounts'}
                  </button>
                  {canManage && (
                    <button
                      className="danger row-end"
                      disabled={busy !== null}
                      onClick={() => {
                        if (window.confirm(
                          `Disconnect QuickBooks from ${client.name}? Fiscal Founders will stop being able to read these books.`,
                        )) void act('disconnect', () => api.disconnect(clientId));
                      }}
                    >
                      Disconnect
                    </button>
                  )}
                </div>

                {readout && (
                  <div style={{ marginTop: '1.1rem' }}>
                    <div className="row" style={{ marginBottom: '.5rem' }}>
                      <h3 style={{ margin: 0 }}>{readout.title}</h3>
                      <button className="link row-end" onClick={() => setReadout(null)}>Clear</button>
                    </div>
                    <pre className="data">{JSON.stringify(readout.body, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
